import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/app/duties/leave-requests/$id")({
  component: ReviewLeaveRequestPage,
  beforeLoad: (context) => {
    return {
      getTitle: () => context.context.ts.absences.t("ReviewAbsenceRequest"),
    }
  },
})

import { Tabs, Stack, Text, Title, Space, Paper } from "@mantine/core"
import { ActivitiesAndBasicCasts } from "../../../../components/absences/activities-and-basic-cast"
import { PropertiesHeader } from "../../../../components/absences/properties-header"
import { ReviewersCard } from "../../../../components/absences/review-list"
import { ArrowList } from "../../../../-components/arrow-list"
import { DefaultButton } from "../../../../-components/Buttons"
import { useEnvironment } from "../../../../-components/useEnvironment"
import { Link } from "../../../../-components/link"
import { ScrollContainer } from "../../../../-components/scroll-container"
import { SuccessAlert } from "../../../../-components/success-alert"
import { useCommand } from "../../../../typed-client/hooks/use-command"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { useQueryGet } from "../../../../typed-client/hooks/use-query-get"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { useTranslations } from "../../../../utils/use-t"
import { AddApprovalStepModal } from "./-components/add-approval-step-modal"
import { Hint } from "./-components/hint"
import { VacationEntitlementForLeaveRequest } from "../../my/absences/-components/vacation-entitlement"
import { P, match } from "ts-pattern"
import { ReviewHistory } from "./-components/review-history"
import { SetOrChangeFinalDrawer } from "./-components/set-or-change-final-decision-drawer"
import { Page } from "../../../../-components/Page"
import { DutyRosterForExistingLeaveRequest } from "../../../../components/absences/absence-duty-roster"

function ReviewLeaveRequestPage() {
  const params = Route.useParams()
  const id = parseInt(params.id)
  const ts = useTranslations()

  const { data, refetch } = useSuspenseQueryGet(
    "/api/Absence/GetApprovalRequestForReview",
    { id }
  )

  const { data: timeAccountInfo } = useQueryGet(
    "/api/Absence/GetTimeAccountBalanceInPeriodForLeaveRequest",
    { leaveRequestId: id }
  )

  const sendRequest = useCommand({
    url: "/api/Absence/Command",
    entityId: id,
    options: {
      onSuccess: () => {
        refetch()
      },
    },
  })
  const [showAddApprovalNode, setShowAddApprovalModalOpened] =
    React.useState(false)
  const navigate = Route.useNavigate()
  const [setOrChangeFinalDecisionMode, setSetOrChangeFinalDecisionMode] =
    React.useState<"closed" | "set" | "change">("closed")

  const { xs } = useBreakpoints()
  const { isDev } = useEnvironment()

  return (
    <Page
      buttonGroupProps={
        xs
          ? undefined
          : {
              children: (
                <>
                  {data.permissions.canChangeFinalDecision ? (
                    <DefaultButton
                      onClick={() => setSetOrChangeFinalDecisionMode("change")}
                    >
                      {ts.absences.t("ChangeFinalDecision")}
                    </DefaultButton>
                  ) : null}
                  {data.permissions.canSetFinal && !data.isFinalNode ? (
                    <DefaultButton
                      onClick={() => setSetOrChangeFinalDecisionMode("set")}
                    >
                      {ts.absences.t("DecideFinally")}
                    </DefaultButton>
                  ) : null}
                </>
              ),
            }
      }
      dottedMenuProps={{
        items: [
          ...(xs
            ? [
                ...(data.permissions.canChangeFinalDecision
                  ? [
                      {
                        children: ts.absences.t("ChangeFinalDecision"),
                        onClick: () =>
                          setSetOrChangeFinalDecisionMode("change"),
                      },
                    ]
                  : []),
                ...(data.permissions.canSetFinal && !data.isFinalNode
                  ? [
                      {
                        children: ts.absences.t("DecideFinally"),
                        onClick: () => setSetOrChangeFinalDecisionMode("set"),
                      },
                    ]
                  : []),
              ]
            : []),
          ...(data.permissions.isAdmin && isDev
            ? [
                {
                  children: "Debug view",
                  onClick: () =>
                    navigate({
                      to: "/app/duties/leave-requests/debug-view/$id",
                      params: { id: params.id },
                    }),
                },
              ]
            : []),
        ],
      }}
    >
      <ScrollContainer direction="column" h="100%">
        <AddApprovalStepModal
          leaveRequestId={id}
          opened={showAddApprovalNode}
          onClose={() => setShowAddApprovalModalOpened(false)}
        />

        <PropertiesHeader size="md" data={data} readOnly={false} />
        <Tabs defaultValue="main" pt="md">
          <Tabs.List>
            <Tabs.Tab value="main">
              {ts.absences.t("ReviewAndApprove")}
            </Tabs.Tab>
            <Tabs.Tab value="history">{ts.appServer.t("History")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="main" pt="md">
            <>
              <Stack>
                <Block title={timeAccountInfo?.label || ""}>
                  <VacationEntitlementForLeaveRequest
                    timeAccountInfo={timeAccountInfo ?? null}
                    creditableValue={data.creditableValues}
                  />
                </Block>
                <Block title={ts.absences.t("AppointmentsInPeriod")}>
                  <ActivitiesAndBasicCasts
                    data={data.activitiesAndBasicCastsInPeriod}
                  />
                </Block>

                {data.showDutyRoster ? (
                  <DutyRosterForExistingLeaveRequest leaveRequestId={id} />
                ) : null}
                {match(data.currentNode?.[0].details)
                  .with(
                    { Case: "Approval", Fields: P.select() },
                    ({ noteForApprovers }) =>
                      noteForApprovers ? <Hint hint={noteForApprovers} /> : null
                  )
                  .otherwise(() => null)}
                <ReviewersCard
                  leaveRequestId={id}
                  data={data}
                  onAddApprovalStepClicked={() => {
                    setShowAddApprovalModalOpened(true)
                  }}
                />
              </Stack>
              {sendRequest.isSuccess &&
              (sendRequest.variables.command.Case === "ReviewApprovalStep" ||
                sendRequest.variables.command.Case === "SetFinalDecision") ? (
                <SuccessAlert
                  title={
                    ts.absences.t("ApprovalStep") +
                    ((sendRequest.variables.command.Case ===
                      "ReviewApprovalStep" &&
                      sendRequest.variables.command.Fields.review.decision
                        .Case === "Approve") ||
                    (sendRequest.variables.command.Case ===
                      "SetFinalDecision" &&
                      sendRequest.variables.command.Fields.decision.Case ===
                        "Approve")
                      ? ts.appServer.t("Approved_lower")
                      : ts.appServer.t("Rejected_lower"))
                  }
                  description={
                    <Text>
                      <p>{ts.absences.t("ReviewedAcknowledgement")}</p>
                      <p>
                        {data.permissions.canReview
                          ? ts.absences.t("ThereAreMoreApprovalStepsForYou")
                          : data.workflow.result.Case === "Approved"
                            ? ts.absences.t("TheRequestWasApproved")
                            : data.workflow.result.Case === "Rejected"
                              ? ts.absences.t("TheRequestWasRejected")
                              : null}
                      </p>
                    </Text>
                  }
                >
                  <ArrowList>
                    <Link
                      linkText={ts.absences.t("BackToAbsenceRequests")}
                      navigateOptions={{ to: "/app/duties/leave-requests" }}
                    />
                    <Link
                      linkText={ts.appServer.t("BackToStartPage")}
                      navigateOptions={{ to: "/" }}
                    />
                  </ArrowList>
                </SuccessAlert>
              ) : null}
            </>
          </Tabs.Panel>
          <Tabs.Panel value="history" pt="md">
            <ReviewHistory data={data} />
          </Tabs.Panel>
        </Tabs>
      </ScrollContainer>
      <SetOrChangeFinalDrawer
        leaveRequestId={id}
        mode={setOrChangeFinalDecisionMode}
        onClose={() => setSetOrChangeFinalDecisionMode("closed")}
        onSuccess={() => {
          refetch()
        }}
      />
    </Page>
  )
}

function Block({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  const { sm, xs } = useBreakpoints()

  return (
    <Paper
      withBorder
      p={sm || xs ? "xs" : "lg"}
      style={{ flex: 1 }}
      radius="md"
    >
      <Title order={4}>{title}</Title>
      <Space my="xs" />
      {children}
    </Paper>
  )
}
